import {
  booleanAttribute,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IonSegment } from '@ionic/angular/standalone';

/**
 * Wraps {@link IonSegment} with shared CrowdCTRL layout and optional compact tab sizing.
 * Project {@link IonSegmentButton} children via content; supports {@link FormsModule} two-way bind
 * with `[(ngModel)]` on this host.
 *
 * Uses {@link ViewEncapsulation.None} so compact styles apply to projected segment buttons.
 */
@Component({
  selector: 'crowdctrl-segmented-control',
  standalone: true,
  imports: [IonSegment, FormsModule],
  templateUrl: './segmented-control.component.html',
  styleUrl: './segmented-control.component.css',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SegmentedControlComponent),
      multi: true,
    },
  ],
})
export class SegmentedControlComponent implements ControlValueAccessor {
  /** When true, the segment scrolls horizontally when tabs overflow. */
  @Input({ transform: booleanAttribute }) scrollable = false;

  /**
   * `compact`: shorter segment buttons (default for CrowdCTRL).
   * `default`: leave Ionic default padding and min-height.
   */
  @Input() layout: 'default' | 'compact' = 'compact';

  /** Same contract as {@link IonSegment.ionChange}. */
  @Output() ionChange = new EventEmitter<Event>();

  protected innerValue: string | undefined;

  private onChange: (value: string | undefined) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(obj: string | undefined): void {
    this.innerValue = obj;
  }

  registerOnChange(fn: (value: string | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected onInnerModelChange(v: string | undefined): void {
    this.innerValue = v;
    this.onChange(v);
  }

  protected onSegmentIonChange(ev: Event): void {
    this.onTouched();
    this.ionChange.emit(ev);
  }
}
